import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { action, computed, makeObservable, observable, override, reaction, runInAction } from 'mobx';
import moment from 'moment';

import { ABAS_DETALHES_PROCESSO } from '@/dominio/AbasDetalhesProcesso';
import { RESULTADO_JULGAMENTO } from '@/dominio/ResultadoJulgamento';
import HtmlDiff from '@/util/HtmlDiff';
import { LOCALSTORAGE_STORAGE } from '@/util/StorageUtil';

import FiltrosSalaPreSessao from '../components/FiltrosSalaPreSessao';
import ApmVars from '../util/ApmVars';
import Configuracao from '../util/Configuracao';
import ErrorUtil from '../util/ErrorUtil';
import SentryUtil from '../util/SentryUtil';
import StringUtil from '../util/StringUtil';
import { VersaoMinutaVoto, VersaoMinutaVotoControleAcesso, db } from './db';
import Store from './Store';

// eslint-disable-next-line unicorn/no-top-level-side-effects
dayjs.extend(customParseFormat);

export const SITUACAO_CARREGAMENTO_VERSAO_MINUTA = {
    CARREGADO_DO_CACHE: 'Carregado do cache',
    EM_ANDAMENTO: 'Carregando',
    FINALIZADO: 'Carregado',
    FALHOU: 'Falhou'
};

export const MODO_CARREGAMENTO_VERSOES_MINUTA = {
    TODAS: 'Todos os conteúdos',
    APENAS_VERSOES_COM_ETAG_DIFERENTES: 'Apenas os conteúdos com eTag diferentes',
    SOB_DEMANDA: 'Apenas se o conteúdo for requisitado pelo usuário'
};

/**
 * Classe utilizada para guardar e manipular o estado da aplicação. Deve-se
 * criar uma para cada contexto ou entidade.
 */
export default class PreSessaoStore extends Store {
    fixarPainelJulgamento = false;
    mensagemErroEnvioLote = [];
    abrirMensagemErroLote = false;
    quantidadeErroMovimento = 0;
    quantidadeErroCertidao = 0;
    quantidadeErroAcordao = 0;
    modalConfirmacaoEditarVoto = false;
    confirmarEdicaoVoto = true;
    painelInicioCertidaoExpandido = true;
    painelDecisaoCertidaoExpandido = true;
    painelPartesCertidaoExpandido = true;
    exibirProcessosComCertidao = false;
    modalCertidaoAberto = false;
    controleCarregamentoVersoesMinutaVoto = [];
    autoTexto = [];

    constructor(props) {
        super(props);

        makeObservable(this, {
            fixarPainelJulgamento: observable,
            mensagemErroEnvioLote: observable,
            abrirMensagemErroLote: observable,
            quantidadeErroMovimento: observable,
            quantidadeErroCertidao: observable,
            quantidadeErroAcordao: observable,
            modalConfirmacaoEditarVoto: observable,
            confirmarEdicaoVoto: observable,
            painelInicioCertidaoExpandido: observable,
            painelDecisaoCertidaoExpandido: observable,
            painelPartesCertidaoExpandido: observable,
            exibirProcessosComCertidao: observable,
            modalCertidaoAberto: observable,
            controleCarregamentoVersoesMinutaVoto: observable,
            autoTexto: observable,
            objetoLocalStorage: computed,
            abrirModalConfirmacaoEditarVoto: action,
            fecharModalConfirmacaoEditarVoto: action,
            toggleFixarPainelJulgamento: action,
            resetAtributosProcessos: action,
            carregandoPartes: computed,
            carregandoJulgamentosAnteriores: computed,
            idSessaoSelecionada: computed,
            descricaoProcessosEmExibicao: computed,
            processosValidadosPorRelator: computed,
            processosPendentesPorRelator: computed,
            processos: computed,
            tipoResultadoJulgamentoJulgado: computed,
            processosEmExibicao: computed,
            carregarTranscendenciaBloqueioBaixa: action,
            carregarConteudoVoto: action,
            consultarVersoesMinutaVoto: action,
            carregarConteudosVersoesEmModoAssincrono: action,
            mesclarDadosVersoesMinutaVoto: action,
            consultarVersaoMinutaVoto: action,
            calcularDiffs: action,
            calcularEstatisticasMudancasTexto: action,
            carregarEditorVoto: action,
            carregarEmentaDecisao: action,
            orgaosJulgadoresColegiadosUsuario: computed,
            handleChangeSessao: action,
            abrirProcessoPeloNumero: action,
            exibirProcessoPeloIndice: action,
            posicionarProcessosEmExibicaoPeloIndice: action,
            indiceProcessoExpandido: computed,
            expandirProximoProcesso: action,
            expandirProcessoAnterior: action,
            salvarRascunhoCertidao: action,
            salvarComplementoAnotacao: action,
            salvarAnotacao: action,
            salvarOrdenacaoSecretario: action,
            salvarSequenciaPreferenciaArbitraria: action,
            recarregarProcesso: action,
            recarregarListaProcessos: action,
            recarregarListaProcessosSemCache: action,
            exibirListaProcessos: action,
            magistrados: computed,
            renumerarObservacoes: action,
            getTipoResultadoJulgamento: action,
            opcoesRelatores: computed,
            isSessaoFinalizada: computed,
            isPermitidaApreciacaoProcessos: computed,
            isPermitidaApreciacaoProcessosPJe: computed,
            carregarMovimentosDecisoesSessao: action,
            recuperarUrlEdicaoVoto: action,
            carregarAutoTexto: action,
            reset: override,
            processosFiltrados: override,
            mapaProcessosPartes: override
        });

        this.createReaction(
            reaction(
                () => this.idProcessosEmExibicao,
                () => (this.processoScroll = undefined)
            )
        );
        this.createReaction(
            reaction(() => this.sessaoStore.sessaoSelecionada, this.carregarInformacoesSessaoSelecionada)
        );
        this.createReaction(
            reaction(
                () => this.processoModal,
                (processoModal) => {
                    if (processoModal) {
                        this.carregarDetalhes(processoModal);
                    }
                }
            )
        );
        this.createReaction(
            reaction(
                () => this.processoExpandido,
                (processoExpandido) => {
                    if (processoExpandido) {
                        this.carregarDetalhes(processoExpandido);
                    }
                }
            )
        );
    }

    get storageType() {
        return LOCALSTORAGE_STORAGE;
    }

    async inicializar() {
        super.inicializar();
        this.inicializarFiltros(() => new FiltrosSalaPreSessao(this));
    }

    get objetoLocalStorage() {
        return {
            nomeStore: 'PreSessaoStore',
            confirmarEdicaoVoto: this.confirmarEdicaoVoto === undefined ? true : this.confirmarEdicaoVoto,
            fecharProgressoAutomaticamente:
                this.fecharProgressoAutomaticamente === undefined ? false : this.fecharProgressoAutomaticamente,
            painelInicioCertidaoExpandido: this.painelInicioCertidaoExpandido,
            painelDecisaoCertidaoExpandido: this.painelDecisaoCertidaoExpandido,
            painelPartesCertidaoExpandido: this.painelPartesCertidaoExpandido
        };
    }

    abrirModalConfirmacaoEditarVoto() {
        this.modalConfirmacaoEditarVoto = true;
    }

    fecharModalConfirmacaoEditarVoto() {
        this.modalConfirmacaoEditarVoto = false;
    }

    toggleFixarPainelJulgamento() {
        this.fixarPainelJulgamento = !this.fixarPainelJulgamento;
    }

    reset() {
        super.reset();
        this.resetAtributosProcessos();
    }

    resetAtributosProcessos() {
        this.sessaoStore.processoApregoado = undefined;
        this.sessaoStore.relatorPregaoLote = undefined;
        this.processoExpandido = undefined;
        this.processoModal = undefined;
        this.processoScroll = undefined;
        this.idProcessosEmExibicao = 'P';
        this.abaSelecionadaDetalhesProcesso = ABAS_DETALHES_PROCESSO.DECISAO.id;
        this.offsetScroll = {};
        this.carregandoMovimentosDecisoes = false;
    }

    get carregandoPartes() {
        return this.sessaoStore.carregandoPartes;
    }

    get carregandoJulgamentosAnteriores() {
        return this.sessaoStore.carregandoJulgamentosAnteriores;
    }

    get idSessaoSelecionada() {
        return this.sessaoStore && this.sessaoStore.idSessaoSelecionada;
    }

    carregarDetalhes(processo) {
        this.carregarDetalhesProcesso(processo);
        this.carregarAlertasImpedimento(processo);
    }

    carregarInformacoesSessaoSelecionada = () => {
        this.configurarProcessoExpandido();
        this.sessaoStore.carregarPartesSessao(this.idSessaoSelecionada);
        this.sessaoStore.carregarJulgamentosAnteriores();
        this.carregarMovimentosDecisoesSessao();
    };

    get opcoesProcessosEmExibicao() {
        return [
            {
                id: 'P',
                ordem: 0,
                descricao: 'Processos',
                descricaoSingular: 'Pré-Sessão',
                descricaoAbreviada: 'pre',
                funcaoProcessos: () => this.processos,
                funcaoProcessosFiltrados: () => this.processosFiltrados
            }
        ];
    }

    get descricaoProcessosEmExibicao() {
        let opcaoProcessosEmExibicao = this.opcoesProcessosEmExibicao.find(
            (opcaoProcessos) => opcaoProcessos.id === this.idProcessosEmExibicao
        );
        return opcaoProcessosEmExibicao ? opcaoProcessosEmExibicao.descricao : '';
    }

    processosAnalisadosRelator(siglaRelator) {
        if (siglaRelator) {
            return this.processosValidadosPorRelator.get(siglaRelator);
        }
        return [];
    }

    get processosValidadosPorRelator() {
        let retorno = new Map();
        this.relatoresSessao.forEach((relator) => {
            retorno.set(relator, this.filtros.filtroNomeRelator.filtrar(this.processos, relator));
        });
        return retorno;
    }

    processosPendentesRelator(relator) {
        if (relator) {
            return this.processosPendentesPorRelator.get(relator);
        }
        return [];
    }

    get processosPendentesPorRelator() {
        let retorno = new Map();
        this.relatoresSessao.forEach((relator) => {
            retorno.set(relator, this.filtros.filtroNomeRelator.filtrar(this.processosFiltrados, relator));
        });
        return retorno;
    }

    isMagistradoImpedido(siglaMagistrado, registro) {
        return this.sessaoStore.isMagistradoImpedido(siglaMagistrado, registro);
    }

    getImpedimentosComposicaoPadrao(registro) {
        let retorno = [];

        if (this.sessaoStore.sessaoSelecionada.composicaoPadrao) {
            retorno = this.sessaoStore.sessaoSelecionada.composicaoPadrao.reduce((acumulador, composicao) => {
                if (composicao.titular && this.isMagistradoImpedido(composicao.titular.sigla, registro)) {
                    acumulador.push({
                        sigla: composicao.titular.sigla,
                        genero: composicao.titular.genero
                    });
                }
                return acumulador;
            }, []);
        }
        return retorno;
    }

    get processos() {
        let processos = [];

        if (this.processosSessao) {
            processos = this.processosSessao;
            processos = this.ordenarPorOrdemPreSessao(processos);
        }
        return processos;
    }

    ordenarPorOrdemPreSessao(processos) {
        return processos.slice().sort((r1, r2) => {
            return r1.ordemPreSessao - r2.ordemPreSessao;
        });
    }

    get processosFiltrados() {
        return this.filtros && this.filtros.filtrarProcessos(this.processos);
    }

    removerDuplicados = (array, propriedade) => {
        return array.filter((obj, pos, arr) => {
            return arr.map((mapObj) => mapObj[propriedade]).indexOf(obj[propriedade]) === pos;
        });
    };

    possuiDestaque(registro) {
        return this.filtros.filtroDestaques.getValorOpcao(registro);
    }

    filtrarPorOrigem(processos, origem) {
        return this.filtros['filtroOrigem'].filtrar(processos, origem);
    }

    get tipoResultadoJulgamentoJulgado() {
        return this.getTipoResultadoJulgamento(RESULTADO_JULGAMENTO.JULGADO.codigo);
    }

    get processosEmExibicao() {
        return this.processosFiltrados;
    }

    async carregarTranscendenciaBloqueioBaixa(registro) {
        if (registro) {
            runInAction(() => {
                registro.carregandoIndicadoresProcesso = true;
            });

            try {
                let info = await this.processoService.recuperarInfoTranscendenciaBloqueioBaixa(
                    this.idSessaoSelecionada,
                    registro
                );

                runInAction(() => {
                    if (info) {
                        registro.bloqueioBaixa = info.bloqueioBaixaAutomatica;
                        registro.transcendencia = info.transcendencia;
                    }

                    if (registro.bloqueioBaixa == null) {
                        const indicadores = registro.processo?.indicadores;

                        registro.bloqueioBaixa =
                            indicadores?.some((ind) => ind.descricao === 'Remessa bloqueada') ?? false;
                    }
                });
            } finally {
                runInAction(() => {
                    registro.carregandoIndicadoresProcesso = false;
                });
            }
        }
    }

    async carregarConteudoVoto(registro) {
        if (registro && registro.processo && registro.processo.numeroCompleto) {
            let conteudoVoto;

            if (registro.acordaoAssinado) {
                conteudoVoto = await this.processoService.recuperarConteudoAcordaoAssinado(
                    registro.processo.numeroCompleto,
                    registro.sessaoId
                );
            } else {
                conteudoVoto = await this.processoService.recuperarConteudoVoto(
                    registro.processo.numeroCompleto,
                    registro.sessaoId
                );
            }

            runInAction(() => (registro.conteudoVoto = conteudoVoto));
        }
    }

    /**
     * @param {Object} versaoMinutaGed
     * @param {*} idDbLocal
     * @returns
     */
    #metadadosGedToString(versaoMinutaGed, idDbLocal = undefined) {
        if (idDbLocal) {
            return `id:${idDbLocal}|gedId:${versaoMinutaGed.docId}|extensaoVersaoGed:${versaoMinutaGed.extensao}`;
        } else {
            return `gedId:${versaoMinutaGed.docId}|extensaoVersaoGed:${versaoMinutaGed.extensao}`;
        }
    }

    #getChaveUnicaDocGed(versaoMinuta) {
        return `${versaoMinuta.docId}|${versaoMinuta.extensao}`;
    }

    async consultarVersoesMinutaVoto(registro, tipoCarregamentoVersoes = MODO_CARREGAMENTO_VERSOES_MINUTA.TODAS) {
        try {
            if (!registro) {
                throw new ReferenceError(
                    `Parâmetro inválido para \`consultarVersoesMinutaVoto\`: registro não pode ser nulo.`
                );
            }

            if (!registro.sessaoId) {
                throw new ReferenceError(
                    `Parâmetro inválido para \`consultarVersoesMinutaVoto\`: sessaoId não pode ser nulo.`
                );
            }

            if (!registro.processo.numeroCompleto) {
                throw new ReferenceError(
                    `Parâmetro inválido para \`consultarVersoesMinutaVoto\`: processo.numeroCompleto não pode ser nulo.`
                );
            }

            let resposta = undefined;

            try {
                resposta = await this.sessaoService.consultarVersoesMinutaVoto(
                    registro.sessaoId,
                    registro.processo.numeroCompleto
                );
            } catch (error) {
                throw new Error(`Serviço de consulta de minutas de votos não respondeu.`, { cause: error });
            }

            if (!resposta) {
                throw new Error(`Serviço de consulta de versões de minutas de votos respondeu de forma inesperada.`);
            }

            let versoesMinuta = JSON.parse(JSON.stringify(resposta));
            console.log(`Total de versões da minuta de voto (resposta do backend): ${versoesMinuta.length}`);

            // Garante que as versões estejam ordenadas corretamente (EM ORDEM DECRESCENTE)
            versoesMinuta.sort((v1, v2) => {
                const dataV1 = dayjs(v1.ultimaAlteracao, Configuracao.FORMATO_DATAHORA_SERVICOS_PADRAO);
                const dataV2 = dayjs(v2.ultimaAlteracao, Configuracao.FORMATO_DATAHORA_SERVICOS_PADRAO);
                return dataV2.diff(dataV1);
            });

            const QUANTIDADE_MINIMA_DE_CONTEUDOS_A_CARREGAR = 2; // Necessário carregar pelo menos as duas primeiras versões para calcular o diff
            const temHash = (versaoMinuta) => !!versaoMinuta.etag;
            const peloMenosUmaVersaoNaoTemHash = !versoesMinuta.every(temHash);

            if (peloMenosUmaVersaoNaoTemHash) {
                console.log(`Pelo menos uma versão de minuta não tem hash (eTag).`);
            }

            let versoesComAlteracaoDeConteudo;

            if (
                tipoCarregamentoVersoes === MODO_CARREGAMENTO_VERSOES_MINUTA.TODAS ||
                versoesMinuta.length <= QUANTIDADE_MINIMA_DE_CONTEUDOS_A_CARREGAR ||
                peloMenosUmaVersaoNaoTemHash
            ) {
                versoesMinuta = await this.carregarConteudosVersoesEmModoAssincrono(registro, versoesMinuta);
                versoesComAlteracaoDeConteudo = versoesMinuta.filter((versao) => !versao.semAlteracaoConteudo);
                console.log(
                    `Total de versões de minuta de voto distintas (de acordo com a comparação de conteúdo): ${versoesComAlteracaoDeConteudo.length}`
                );
            } else {
                for (let i = 0; i < versoesMinuta.length - 1; i++) {
                    if (versoesMinuta[i].etag && versoesMinuta[i + 1].etag) {
                        if (versoesMinuta[i].etag === versoesMinuta[i + 1].etag) {
                            versoesMinuta[i].semAlteracaoConteudo = true;
                        }
                    }
                }

                const versoesComAlteracaoDeConteudoDeAcordoComHash = versoesMinuta.filter(
                    (versao) => !versao.semAlteracaoConteudo
                );
                console.log(
                    `Total de versões de minuta de voto distintas (de acordo com a comparação de hash): ${versoesComAlteracaoDeConteudoDeAcordoComHash.length}`
                );
                let versoesCarregadas;

                if (tipoCarregamentoVersoes === MODO_CARREGAMENTO_VERSOES_MINUTA.APENAS_VERSOES_COM_ETAG_DIFERENTES) {
                    versoesCarregadas = await this.carregarConteudosVersoesEmModoAssincrono(
                        registro,
                        versoesComAlteracaoDeConteudoDeAcordoComHash,
                        versoesComAlteracaoDeConteudoDeAcordoComHash.length
                    );
                } else if (tipoCarregamentoVersoes === MODO_CARREGAMENTO_VERSOES_MINUTA.SOB_DEMANDA) {
                    versoesCarregadas = await this.carregarConteudosVersoesEmModoAssincrono(
                        registro,
                        versoesComAlteracaoDeConteudoDeAcordoComHash,
                        QUANTIDADE_MINIMA_DE_CONTEUDOS_A_CARREGAR
                    );
                }

                versoesComAlteracaoDeConteudo = versoesCarregadas.filter((versao) => !versao.semAlteracaoConteudo);
                console.log(
                    `Total de versões de minuta de voto distintas (de acordo com a comparação de conteúdo): ${versoesComAlteracaoDeConteudo.length}`
                );

                if (versoesComAlteracaoDeConteudoDeAcordoComHash.length !== versoesComAlteracaoDeConteudo.length) {
                    console.warn(`Totais diferentes! O serviço retornou hashes diferentes para conteúdos iguais!`);
                }
            }

            versoesMinuta = JSON.parse(JSON.stringify(versoesComAlteracaoDeConteudo));
            versoesMinuta = await this.mesclarDadosVersoesMinutaVoto(registro, versoesMinuta);
            versoesMinuta = this.calcularDiffs(versoesMinuta);
            runInAction(() => (registro.versoesMinutaVoto = JSON.parse(JSON.stringify(versoesMinuta))));
        } catch (error) {
            runInAction(() => (registro.versoesMinutaVoto = null));
            throw new Error(`Falha inesperada ao tentar carregar as versões da minuta de voto.`, { cause: error });
        }
    }

    async carregarConteudosVersoesEmModoAssincrono(registro, versoesMinuta, totalVersoesACarregar) {
        if (!totalVersoesACarregar) {
            totalVersoesACarregar = versoesMinuta.length;
        }

        const promisesMap = new Map();

        // Carrega as versões para limpar registros de alterações exclusivamente em metadados de minutas (sem alterações nos textos)
        let contador = 0;
        versoesMinuta.forEach((versao) => {
            if (++contador <= totalVersoesACarregar) {
                const chave = this.#getChaveUnicaDocGed(versao);
                const promisseConsulta = this.consultarVersaoMinutaVoto(registro, versao, false); // Não grava no cache local na momento da consulta para armazenar a informação "semAlteracaoConteudo" corretamente
                promisesMap.set(chave, promisseConsulta);
            }
        });

        const versoesMinutaVotoComConteudosCarregados = await Promise.all(Array.from(promisesMap.values()));

        if (totalVersoesACarregar !== versoesMinutaVotoComConteudosCarregados.length) {
            console.warn(
                `O total de versões carregadas é diferente do total de versões esperadas (foram carregadas ${versoesMinutaVotoComConteudosCarregados.length}/${totalVersoesACarregar} versões de minutas).`
            );
            //throw new Error(`O total de versões carregadas é diferente do total de versões esperadas (foram carregadas ${versoesMinutaVotoComConteudosCarregados.length}/${totalVersoesACarregar} versões de minutas).`);
        }

        // Compara os textos para eliminar as versões duplicadas
        let indice = 0;
        versoesMinuta.forEach((versao) => {
            if (indice < totalVersoesACarregar) {
                if (
                    indice < versoesMinutaVotoComConteudosCarregados.length - 1 &&
                    versoesMinutaVotoComConteudosCarregados[indice].statusCarregamento !==
                        SITUACAO_CARREGAMENTO_VERSAO_MINUTA.FALHOU &&
                    versoesMinutaVotoComConteudosCarregados[indice + 1].statusCarregamento !==
                        SITUACAO_CARREGAMENTO_VERSAO_MINUTA.FALHOU &&
                    versoesMinutaVotoComConteudosCarregados[indice].statusCarregamento !==
                        SITUACAO_CARREGAMENTO_VERSAO_MINUTA.EM_ANDAMENTO &&
                    versoesMinutaVotoComConteudosCarregados[indice + 1].statusCarregamento !==
                        SITUACAO_CARREGAMENTO_VERSAO_MINUTA.EM_ANDAMENTO &&
                    versoesMinutaVotoComConteudosCarregados[indice].conteudo !== undefined &&
                    versoesMinutaVotoComConteudosCarregados[indice + 1].conteudo !== undefined &&
                    versoesMinutaVotoComConteudosCarregados[indice].conteudo ===
                        versoesMinutaVotoComConteudosCarregados[indice + 1].conteudo
                ) {
                    versao.semAlteracaoConteudo = true;
                }
            }

            indice++;
        });

        return versoesMinuta;
    }

    async mesclarDadosVersoesMinutaVoto(registro, versoesMinuta) {
        await versoesMinuta.forEach(async (versao) => {
            console.log(
                `Mesclando informações entre a resposta do serviço e o cache local... [${registro.processo.numeroCompleto}|${this.#metadadosGedToString(versao)}]`
            );
            await this.armazenarVersaoMinutaVotoCache(registro.processo.numeroCompleto, versao);

            if (!versao.conteudo || !versao.semAlteracaoConteudo) {
                const versaoNoCache = await this.consultarCacheLocalVersoesMinutasVotos(
                    registro.processo.numeroCompleto,
                    versao
                );

                if (!versao.conteudo && versaoNoCache && versaoNoCache.conteudo) {
                    versao.conteudo = versaoNoCache.conteudo;
                }

                if (!versao.semAlteracaoConteudo && versaoNoCache && versaoNoCache.semAlteracaoConteudo) {
                    versao.semAlteracaoConteudo = versaoNoCache.semAlteracaoConteudo;
                }
            }
        });

        return versoesMinuta;
    }

    async consultarVersaoMinutaVoto(registro, versaoMinutaGed, armazenarCache = true) {
        if (!registro) {
            throw new ReferenceError(
                `Parâmetro inválido para \`consultarVersaoMinutaVoto\`: registro não pode ser nulo.`
            );
        }

        if (!versaoMinutaGed) {
            throw new ReferenceError(
                `Parâmetro inválido para \`consultarVersaoMinutaVoto\`: versaoMinutaGed não pode ser nulo.`
            );
        }

        const cache = await this.consultarCacheLocalVersoesMinutasVotos(
            registro.processo.numeroCompleto,
            versaoMinutaGed
        );

        if (cache && cache.conteudo) {
            runInAction(() => {
                versaoMinutaGed.statusCarregamento = SITUACAO_CARREGAMENTO_VERSAO_MINUTA.EM_ANDAMENTO;
            });

            console.log(
                `Atualizando o conteúdo da minuta de voto em memória com o conteúdo encontrado no cache... [${registro.processo.numeroCompleto}|${this.#metadadosGedToString(versaoMinutaGed)}]`
            );
            runInAction(() => {
                versaoMinutaGed.conteudo = cache.conteudo;
                versaoMinutaGed.statusCarregamento = SITUACAO_CARREGAMENTO_VERSAO_MINUTA.CARREGADO_DO_CACHE;
            });
        } else {
            if (
                versaoMinutaGed.statusCarregamento &&
                versaoMinutaGed.statusCarregamento === SITUACAO_CARREGAMENTO_VERSAO_MINUTA.EM_ANDAMENTO
            ) {
                console.log(`Aguardando a finalização do carregamento da versão da minuta...`);
                await this.esperarCarregamentoVersaoMinutaVoto(versaoMinutaGed);
            } else {
                console.log(
                    `Requisitando versão da minuta... [${registro.processo.numeroCompleto}|${this.#metadadosGedToString(versaoMinutaGed)}]`
                );
                runInAction(() => {
                    versaoMinutaGed.statusCarregamento = SITUACAO_CARREGAMENTO_VERSAO_MINUTA.EM_ANDAMENTO;
                });

                try {
                    const resposta = await this.sessaoService.consultarVersaoMinutaVoto(
                        registro.sessaoId,
                        registro.processo.numeroCompleto,
                        versaoMinutaGed.docId,
                        versaoMinutaGed.extensao
                    );

                    if (resposta && resposta.conteudo) {
                        runInAction(() => {
                            versaoMinutaGed.statusCarregamento = SITUACAO_CARREGAMENTO_VERSAO_MINUTA.FINALIZADO;
                            versaoMinutaGed.conteudo = resposta.conteudo;
                        });
                    } else {
                        throw new Error('Conteúdo da versão de minuta indisponível.');
                    }
                } catch (error) {
                    runInAction(() => {
                        versaoMinutaGed.conteudo = '';
                        versaoMinutaGed.statusCarregamento = SITUACAO_CARREGAMENTO_VERSAO_MINUTA.FALHOU;
                        versaoMinutaGed.erroCarregamento = ErrorUtil.getMensagemErro(error);
                    });
                } finally {
                    if (armazenarCache) {
                        await this.armazenarVersaoMinutaVotoCache(registro.processo.numeroCompleto, versaoMinutaGed);
                    }
                }
            }
        }

        return versaoMinutaGed;
    }

    async esperarCarregamentoVersaoMinutaVoto(versaoMinutaVoto) {
        const INTERVALO_VERIFICACAO = 100;

        return await new Promise((resolve) => {
            const verificadorCarregamentoIntervalID = setInterval(verificarCarregamento, INTERVALO_VERIFICACAO);

            function verificarCarregamento() {
                //console.info(`Aguardando carregamento da versão da minuta de voto... [ID=${verificadorCarregamentoIntervalID}]`);
                if (versaoMinutaVoto.conteudo) {
                    console.info(`Conteúdo da versão da minuta carregado. [ID=${verificadorCarregamentoIntervalID}]`);
                    clearInterval(verificadorCarregamentoIntervalID);
                    resolve(true);
                } else if (versaoMinutaVoto.statusCarregamento === SITUACAO_CARREGAMENTO_VERSAO_MINUTA.FALHOU) {
                    console.info(
                        `Erro ao tentar carregar a versão da minuta. [ID=${verificadorCarregamentoIntervalID}]`
                    );
                    clearInterval(verificadorCarregamentoIntervalID);
                    resolve(true);
                }
            }
        });
    }

    async armazenarVersaoMinutaVotoCache(numeroProcesso, versaoMinutaGed) {
        const tupla = new VersaoMinutaVoto(
            undefined,
            versaoMinutaGed.docId,
            versaoMinutaGed.extensao,
            versaoMinutaGed.ultimaAlteracao,
            versaoMinutaGed.etag,
            versaoMinutaGed.conteudo,
            versaoMinutaGed.semAlteracaoConteudo
        );

        const item = await this.consultarCacheLocalVersoesMinutasVotos(numeroProcesso, versaoMinutaGed);

        if (item && item.idVersaoMinuta) {
            console.log(
                `O item já existe no cache local [${this.#metadadosGedToString(versaoMinutaGed, item.idVersaoMinuta)}]`
            );
            tupla.idVersaoMinuta = item.idVersaoMinuta;
            tupla.conteudo = item.conteudo ? item.conteudo : versaoMinutaGed.conteudo;
            tupla.semAlteracaoConteudo = item.semAlteracaoConteudo
                ? item.semAlteracaoConteudo
                : versaoMinutaGed.semAlteracaoConteudo;
        }

        try {
            let id = undefined;

            if (tupla.idVersaoMinuta) {
                id = await db.table('versaoMinutaVoto').put(tupla);
                console.log(
                    `Minuta de voto atualizada no cache com sucesso. [${this.#metadadosGedToString(versaoMinutaGed, id)}]`
                );
            } else {
                id = await db.table('versaoMinutaVoto').add(tupla);
                console.log(
                    `Minuta de voto adicionada ao cache com sucesso. [${this.#metadadosGedToString(versaoMinutaGed, id)}]`
                );
            }

            this.atualizarUltimoAcesso(versaoMinutaGed);
        } catch (error) {
            console.error(`Falha ao gravar a minuta de voto no cache local: ${ErrorUtil.getMensagemErro(error)}`);
        }
    }

    calcularDiffs(versoesMinuta) {
        for (let i = 0; i < versoesMinuta.length; i++) {
            if (!versoesMinuta[i].diff && versoesMinuta[i].conteudo) {
                let conteudoAntigo = '';

                if (i + 1 < versoesMinuta.length && versoesMinuta[i + 1].conteudo) {
                    conteudoAntigo = versoesMinuta[i + 1].conteudo;
                }

                const htmlDiff = HtmlDiff.execute(conteudoAntigo, versoesMinuta[i].conteudo);
                runInAction(() => (versoesMinuta[i].diff = htmlDiff));
                this.calcularEstatisticasMudancasTexto(versoesMinuta[i], htmlDiff);
            }
        }

        return versoesMinuta;
    }

    calcularEstatisticasMudancasTexto(versaoMinuta, htmlDiff = undefined) {
        if (!htmlDiff) {
            htmlDiff = versaoMinuta.htmlDiff ? versaoMinuta.htmlDiff : '';
        }

        // @ts-ignore
        const countInsercoes = (htmlDiff.match(/ins class="diff/g) || []).length;
        // @ts-ignore
        const countExclusoes = (htmlDiff.match(/del class="diff/g) || []).length;
        //const countInsercoes = (htmlDiff.match(/diffins/g) || []).length;
        //const countExclusoes = (htmlDiff.match(/diffdel/g) || []).length;
        //const countAlteracoes = (htmlDiff.match(/diffmod/g) || []).length;

        runInAction(() => {
            if (!versaoMinuta.erroCarregamento) {
                versaoMinuta.mudancas = {
                    totalInsercoes: countInsercoes ? countInsercoes : 0,
                    totalExclusoes: countExclusoes ? countExclusoes : 0
                };
            }
        });
    }

    static async excluirMinutasVotoDoCache(
        tempoExpiracaoMinutos = Configuracao.CACHE_MINUTAS_VOTOS_TEMPO_EXPIRACAO_MINUTOS
    ) {
        const dataHoraReferenciaExpiracaoCache = dayjs().subtract(tempoExpiracaoMinutos, 'minutes');
        console.log(`Data de expiração do cache de minutas de votos: ${dataHoraReferenciaExpiracaoCache.toString()}`);
        let totalItensExcluidos = 0;

        try {
            const itensSelecionadosParaExclusao = await db
                .table('versaoMinutaVotoControleAcesso')
                .filter((acesso) => dayjs(acesso.ultimoAcesso).isBefore(dataHoraReferenciaExpiracaoCache))
                .toArray();

            for (const item of itensSelecionadosParaExclusao) {
                const itensExcluidos = await db
                    .table('versaoMinutaVoto')
                    .filter((versao) => versao.docId === item.docId && versao.extensao === item.extensao)
                    .delete();

                if (itensExcluidos > 0) {
                    totalItensExcluidos++;
                }
            }
        } catch (error) {
            throw new Error(
                `Falha ao excluir dados do cache de minutas de votos: ${ErrorUtil.getMensagemErro(error)}`,
                { cause: error }
            );
        }

        return totalItensExcluidos;
    }

    async atualizarUltimoAcesso(versaoMinutaGed) {
        const tupla = new VersaoMinutaVotoControleAcesso(
            undefined,
            versaoMinutaGed.docId,
            versaoMinutaGed.extensao,
            dayjs().format()
        );

        const item = await db
            .table('versaoMinutaVotoControleAcesso')
            .filter((acesso) => acesso.docId === versaoMinutaGed.docId && acesso.extensao === versaoMinutaGed.extensao)
            .first();

        if (item && item.idControleAcesso) {
            console.log(
                `O item de controle de acesso já existe no db local. [${this.#metadadosGedToString(versaoMinutaGed, item.idControleAcesso)}]`
            );
            tupla.idControleAcesso = item.idControleAcesso;
        }

        try {
            let id = undefined;

            if (tupla.idControleAcesso) {
                id = await db.table('versaoMinutaVotoControleAcesso').put(tupla);
                console.log(
                    `Controle de acesso atualizado no cache com sucesso. [${this.#metadadosGedToString(versaoMinutaGed, id)}]`
                );
            } else {
                id = await db.table('versaoMinutaVotoControleAcesso').add(tupla);
                console.log(
                    `Controle de acesso adicionado ao cache com sucesso. [${this.#metadadosGedToString(versaoMinutaGed, id)}]`
                );
            }
        } catch (error) {
            console.error(`Falha ao gravar o controle de acesso no cache local: ${ErrorUtil.getMensagemErro(error)}`);
            throw error;
        }
    }

    async consultarCacheLocalVersoesMinutasVotos(numeroProcesso, versaoMinutaGed) {
        console.log(
            `Consultando metadados da versão de minuta de voto no cache local... [${numeroProcesso}|${this.#metadadosGedToString(versaoMinutaGed)}]`
        );
        let item = null;

        try {
            item = await db
                .table('versaoMinutaVoto')
                .filter(
                    (versao) => versao.docId === versaoMinutaGed.docId && versao.extensao === versaoMinutaGed.extensao
                )
                .first();

            if (item) {
                console.log(
                    `Achou no cache local. [${this.#metadadosGedToString(versaoMinutaGed, item.idVersaoMinuta)}]`
                );
                this.atualizarUltimoAcesso(versaoMinutaGed);

                if (!item.conteudo) {
                    console.log(
                        `Porém, o conteúdo está vazio. [${this.#metadadosGedToString(versaoMinutaGed, item.idVersaoMinuta)}]`
                    );
                }
            } else {
                console.log(`NÃO achou no cache local. [${this.#metadadosGedToString(versaoMinutaGed)}]`);
            }
        } catch (error) {
            throw new Error(`Falha ao consultar o cache local: ${ErrorUtil.getMensagemErro(error)}`, { cause: error });
        }

        return item;
    }

    async carregarEditorVoto(registro) {
        console.log(
            `Consultando editor utilizado para elaborar a minuta de voto... [${registro.processo.numeroCompleto}]`
        );

        if (registro && registro.processo && registro.processo.numeroCompleto) {
            const editorMinutaVoto = await this.processoService.recuperarEditorVoto(
                registro.processo.numeroCompleto,
                registro.sessaoId
            );
            runInAction(() => (registro.editorMinutaVoto = editorMinutaVoto));
        }
    }

    async carregarEmentaDecisao(registro) {
        console.log(`Consultando ementa e decisão... [${registro.processo.numeroCompleto}]`);
        let ementaDecisao;
        let planilha;

        if (registro && registro.processo && registro.processo.numeroCompleto) {
            ementaDecisao = await this.processoService.recuperarEmentaDecisao(
                registro.numeroProcesso,
                registro.sessaoId
            );

            if (this.isComponenteHabilitado('PlanilhaPlenario')) {
                console.log(`Consultando ementa e decisão da planilha... [${registro.processo.numeroCompleto}]`);
                planilha = await this.processoService.recuperarPlanilha(registro.processo.numeroCompleto);
            }

            runInAction(() => {
                if (ementaDecisao) {
                    registro.ementaDecisaoVoto = undefined;
                    registro.observacaoGabinete = undefined;
                    registro.ementa = this.retirarTagsHtmlDaEmenta(ementaDecisao.ementa);
                    registro.decisao = this.retirarTagsHtmlDaDecisao(ementaDecisao.decisao);

                    if (planilha) {
                        if (planilha.ementa || planilha.decisao) {
                            registro.ementaDecisaoVoto = {};
                            registro.ementaDecisaoVoto.ementa = this.retirarTagsHtmlDaEmenta(ementaDecisao.ementa);
                            registro.ementaDecisaoVoto.decisao = this.retirarTagsHtmlDaDecisao(ementaDecisao.decisao);
                        }

                        registro.ementa = planilha.ementa ? this.retirarTagsHtmlDaEmenta(planilha.ementa) : '';
                        registro.decisao = planilha.decisao ? this.retirarTagsHtmlDaDecisao(planilha.decisao) : '';
                        registro.observacaoGabinete = planilha.observacao
                            ? this.retirarTagsHtmlDaObservacao(planilha.observacao)
                            : '';
                    }
                }
            });
        }
        return ementaDecisao;
    }

    retirarTagsHtmlDaDecisao(decisao) {
        decisao = this.retirarTags(decisao);
        return decisao ? decisao : '';
    }

    retirarTagsHtmlDaEmenta(ementa) {
        ementa = this.retirarTags(ementa);
        return ementa ? ementa : '';
    }

    retirarTagsHtmlDaObservacao(observacao) {
        observacao = this.retirarTags(observacao);
        return observacao ? observacao : '';
    }

    retirarTags(texto) {
        let tmp = document.implementation.createHTMLDocument('New').body;
        tmp.innerHTML = texto;
        let styles = tmp.getElementsByTagName('style');

        if (styles.length > 0) {
            tmp.getElementsByTagName('style')[0].remove();
        }
        return tmp.textContent || tmp.innerText || '';
    }

    get orgaosJulgadoresColegiadosUsuario() {
        return this.usuarioStore.usuario.orgaosJulgadoresColegiados || [];
    }

    handleChangeSessao = (event) => {
        this.sessaoStore.idSessaoSelecionada = event.target.value;
    };

    abrirProcessoPeloNumero = (numeroProcesso) => {
        let registro = this.processosSessao.find((reg) => reg.numeroProcesso === numeroProcesso);

        if (registro) {
            ApmVars.setTagNumeroProcesso(numeroProcesso);
            SentryUtil.setScope();
            this.abrirProcesso(registro);
        }
    };

    // Abre o processo e posiciona a listagem no processo
    exibirProcessoPeloIndice(index) {
        if (this.processosEmExibicao.length > 0) {
            index = index >= 0 ? index : 0;
            index = index > this.processosEmExibicao.length - 1 ? this.processosEmExibicao.length - 1 : index;
            this.processoExpandido = this.processosEmExibicao[index];
            this.processoScroll = this.processosEmExibicao[index];
        }
    }

    // Posiciona a listagem no processo a partir do índice
    posicionarProcessosEmExibicaoPeloIndice(index) {
        if (this.processosEmExibicao.length > 0) {
            index = index >= 0 ? index : 0;
            index = index > this.processosEmExibicao.length - 1 ? this.processosEmExibicao.length - 1 : index;
            this.processoScroll = this.processosEmExibicao[index];
        }
    }

    get indiceProcessoExpandido() {
        let indiceProcessoExpandido = -1;

        if (this.processoExpandido) {
            indiceProcessoExpandido = this.processosEmExibicao.findIndex(
                (registro) =>
                    // @ts-ignore
                    registro.processo.numeroCompleto.indexOf(this.processoExpandido.processo.numeroCompleto) !== -1
            );
        }

        return indiceProcessoExpandido;
    }

    expandirProximoProcesso() {
        this.exibirProcessoPeloIndice(this.indiceProcessoExpandido + 1);
    }

    expandirProcessoAnterior() {
        this.exibirProcessoPeloIndice(this.indiceProcessoExpandido - 1);
    }

    async salvarRascunhoCertidao(registro, conteudo) {
        conteudo = StringUtil.tratarCaracteresNaoLatin1(conteudo);
        await this.certidaoService.salvarRascunhoCertidao(registro, conteudo);
        runInAction(() => {
            registro.rascunhoCertidao = conteudo;
            registro.rascunhoEmEdicao = false;
        });
    }

    salvarComplementoAnotacao(registro, anotacoes) {
        return this.anotacaoService.salvarComplementoAnotacao(
            registro.sessaoId,
            registro.processo.numeroCompleto,
            anotacoes
        );
    }

    salvarAnotacao(registro, anotacoes) {
        return this.anotacaoService.salvarAnotacao(registro.sessaoId, registro.processo.numeroCompleto, anotacoes);
    }

    salvarOrdenacaoSecretario(registro, ordenacaoSecretario) {
        return this.sessaoService.salvarOrdenacaoSecretario(
            registro.sessaoId,
            registro.processo.numeroCompleto,
            ordenacaoSecretario
        );
    }

    salvarSequenciaPreferenciaArbitraria(registro, sequenciaPreferencia) {
        return this.sessaoService.salvarSequenciaPreferenciaArbitraria(
            registro.sessaoId,
            registro.processo.numeroCompleto,
            sequenciaPreferencia
        );
    }

    async recarregarProcesso(registro, ignorarCacheServidor = false, aguardarCarregamento = false) {
        if (aguardarCarregamento) {
            await this.sessaoStore.recarregarProcesso(registro, ignorarCacheServidor);
        } else {
            this.sessaoStore.recarregarProcesso(registro, ignorarCacheServidor);
        }
        console.log('PreSessaoStore - recarregarPRocesso');
    }

    async recarregarListaProcessos() {
        await this.sessaoStore.recarregarListaProcessos();
    }

    async recarregarListaProcessosSemCache() {
        await this.sessaoStore.recarregarListaProcessosSemCache();
    }

    exibirListaProcessos() {
        this.idProcessosEmExibicao = 'P';
    }

    getProximoProcessoLista(numeroProcesso, processos) {
        let indiceProcessoAtual = processos.findIndex(
            (reg) => reg.processo.numeroCompleto.indexOf(numeroProcesso) > -1
        );
        let indiceProximoProcesso;

        if (processos.length > 1) {
            if (indiceProcessoAtual === processos.length - 1) {
                indiceProximoProcesso = indiceProcessoAtual - 1;
            } else {
                indiceProximoProcesso = indiceProcessoAtual + 1;
            }
        }

        return processos[indiceProximoProcesso];
    }

    // Só registra a observação se houver texto correspondente ao tipoJulgamento
    // em Configuracao.TXT_CERTIDAO_RESULTADOS
    async registrarObservacaoRascunhoCertidao(registro, tipoResultadoJulgamento, vistas) {
        let complementos = [];

        switch (tipoResultadoJulgamento.id) {
            case RESULTADO_JULGAMENTO.ADIADO.codigo:
            case RESULTADO_JULGAMENTO.RETIRADO_DE_PAUTA.codigo:
            case RESULTADO_JULGAMENTO.SUSPENSO.codigo:
            case RESULTADO_JULGAMENTO.SOBRESTADO.codigo:
                if (registro.processo.relator) {
                    complementos = [
                        this.pronomeTratamentoComCargo(registro.processo.relator),
                        registro.processo.relator.nome || '',
                        this.descricaoRelator(registro.processo.relator)
                    ];
                }
                break;
            case RESULTADO_JULGAMENTO.VISTA_REGIMENTAL.codigo:
                if (registro.processo.relator) {
                    complementos = [
                        this.complementoVistores(vistas),
                        this.pronomeTratamentoComCargo(registro.processo.relator),
                        registro.processo.relator.nome || '',
                        this.descricaoRelator(registro.processo.relator)
                    ];
                }
                break;
            default:
                break;
        }
        await this.complementarRascunhoCertidao(registro, tipoResultadoJulgamento, complementos);
    }

    descricaoRelator(magistrado) {
        let descricao = 'Relator';

        if (!magistrado?.genero) {
            console.error(
                `Falha ao tentar ler o gênero do magistrado ${magistrado.nome} para definição da forma de tratamento do responsável pela relatoria.`
            );
            return descricao;
        }

        return descricao + (magistrado.genero.startsWith('F') ? 'a' : '');
    }

    complementoVistores(vistas) {
        let retorno = '';
        vistas = vistas || [];
        vistas.forEach((vistor, index) => {
            let magistrado = this.magistrados.find((mag) => mag.sigla === vistor.sigla);

            if (magistrado) {
                retorno += this.complementoVistor(magistrado);
                retorno += index === vistas.length - 2 ? ' e ' : ', ';
            } else {
                console.error(
                    'Magistrado ' + vistor + ' não encontrado na lista de magistrados da Secretaria Eletrônica.'
                );
            }
        });
        return retorno;
    }

    get magistrados() {
        return this.rootStore.dominioStore.magistradosComSigla;
    }

    complementoVistor(magistrado) {
        return `pel${this.pronomeTratamentoComCargo(magistrado)} ${magistrado.nome}`;
    }

    pronomeTratamentoComCargo(magistrado) {
        const sigla = magistrado ? magistrado.sigla : '';

        if (magistrado) {
            this.recuperarGeneroMagistrado(sigla, magistrado);
            const artigoGenero = magistrado.genero && magistrado.genero.startsWith('F') ? 'a' : 'o';
            return `${artigoGenero} ${this.montarAbreviacaoExcelentissimo(artigoGenero)} ${this.descricaoCargo(magistrado)}`;
        } else {
            console.error(`Magistrado ${sigla} não encontrado na lista de magistrados da Secretaria Eletrônica.`);
            return ' <MAGISTRADO NÃO CADASTRADO NO PJE> ';
        }
    }

    montarAbreviacaoExcelentissimo(artigoGenero) {
        const excecoesAbreviacao = this.rootStore.parametroStore.getParametro('EXCECOES_ABREVIACAO_EXCELENTISSIMO_ABL');

        if (excecoesAbreviacao && dayjs().isBefore(dayjs(excecoesAbreviacao.dataFimVigencia, 'YYYY-MM-DD'))) {
            if (excecoesAbreviacao && excecoesAbreviacao.valor) {
                const excecoesAbreviacaoArray = excecoesAbreviacao.valor.split(/[,;]/); // Valores separados por ',' ou ';'

                if (excecoesAbreviacaoArray.includes(this.sessaoStore.sessaoSelecionada.orgaoJudicante.sigla)) {
                    return `Exm${artigoGenero}.`;
                }
            }
        }
        return `Ex.m${artigoGenero}`;
    }

    recuperarGeneroMagistrado(sigla, magistrado) {
        if (!magistrado.genero) {
            let magistr = this.dominioStore.magistrados.find((mag) => mag.sigla === sigla);

            if (magistr) {
                magistrado.genero = magistr.genero || 'M';
            }
        }
    }

    descricaoCargo(magistrado) {
        let genero = magistrado.genero || '';
        let siglaCargo = magistrado.codigoCargo || '';
        let cargo = this.rootStore.dominioStore.getDescricaoCargo(siglaCargo, genero);
        return cargo ? cargo.descricaoCargo : '';
    }

    getQuantidadeObservacoes(conteudoCertidao) {
        let retorno = [];
        let re = /Observação \d+/g;
        let s = conteudoCertidao;
        let m;

        do {
            m = re.exec(s);

            if (m) {
                retorno.push(m[0]);
            }
        } while (m);

        return retorno.length;
    }

    renumerarObservacoes(conteudo) {
        let linhas = [];
        let rascunho = conteudo + '\n';
        let reLinha = /.*\n/g;
        let reObservacao = /Observação \d+/g;
        let linha;

        do {
            linha = reLinha.exec(rascunho);

            if (linha) {
                linhas.push(linha[0]);
            }
        } while (linha);

        let index = 0;
        let count = 1;
        let novaLinha = '';

        linhas.forEach((lin) => {
            novaLinha = lin.replace('\n', '');

            if (lin.match(reObservacao)) {
                novaLinha = novaLinha.replace(reObservacao, 'Observação ' + count++);
            }
            linhas[index] = novaLinha;
            index++;
        });

        return linhas.join('\n');
    }

    async complementarRascunhoCertidao(registro, tipoResultadoJulgamento, complementos) {
        let texto = Configuracao.TXT_CERTIDAO_RESULTADOS[tipoResultadoJulgamento.id];

        if (texto) {
            complementos.forEach((complemento, index) => {
                texto = texto.replace('{' + index + '}', complemento);
            });
            await this.adicionarTextoRascunhoCertidao(registro, texto);
        }
    }

    async adicionarTextoRascunhoCertidao(registro, texto) {
        let rascunho = await this.certidaoService.recuperarRascunhoCertidao(registro);
        rascunho += texto;
        await this.certidaoService.salvarRascunhoCertidao(registro, rascunho);
        runInAction(() => (registro.rascunhoCertidao = rascunho));
    }

    async desfazerAlteracaoRascunhoCertidao(registro, texto) {
        let rascunho = registro.rascunho.replace(new RegExp(texto, 'g'), '');
        await this.certidaoService.salvarRascunhoCertidao(registro, rascunho);
        runInAction(() => (registro.rascunhoCertidao = rascunho));
    }

    getTipoResultadoJulgamento(idTipoJulgamento) {
        return this.dominioStore.tiposResultadoJulgamento.find((tipo) => tipo.codigo === idTipoJulgamento);
    }

    get opcoesRelatores() {
        let opcoes = [];
        this.relatoresSessao.forEach((relator) => {
            this.adicionarOpcaoRelator(this.processosPendentesRelator(relator), opcoes, relator);
        });
        opcoes.sort((r1, r2) => (r1.antiguidade > r2.antiguidade ? 0 : -1));
        return opcoes;
    }

    adicionarOpcaoRelator(processos, opcoes, relator) {
        if (processos.length > 0) {
            opcoes.push({
                label: this.getLabelRelator(relator),
                value: relator,
                antiguidade: this.antiguidadeRelator(relator),
                qtdPendentes: processos.length,
                primeiroProcesso: processos[0].processo.numeroCompleto,
                ultimoProcesso: processos[processos.length - 1].processo.numeroCompleto
            });
        }
    }

    isPautaPublicada(registro) {
        let dataPublicacaoPauta =
            this.sessaoStore.sessaoSelecionada.prazosPorSessao[registro.sessaoId].dataPublicacaoPauta;
        return (
            dataPublicacaoPauta &&
            moment(dataPublicacaoPauta, 'DD-MM-YYYY-HH-mm-ss')
                .startOf('day')
                .isSameOrBefore(moment().endOf('day'), 'day')
        );
    }

    get isSessaoFinalizada() {
        if (this.sessaoStore.sessaoSelecionada?.prazos?.dataFimVotacaoVirtual) {
            const dataEncerramentoSessaoObj = dayjs(
                this.sessaoStore.sessaoSelecionada.prazos.dataFimVotacaoVirtual,
                Configuracao.FORMATO_DATAHORA_SERVICOS_PADRAO
            );

            return dataEncerramentoSessaoObj.isBefore(dayjs());
        } else if (this.sessaoStore.sessaoSelecionada?.prazos?.dataInicioSessaoPresencial) {
            const dataEncerramentoSessaoObj = dayjs(
                this.sessaoStore.sessaoSelecionada.prazos.dataInicioSessaoPresencial,
                Configuracao.FORMATO_DATAHORA_SERVICOS_PADRAO
            );

            return dataEncerramentoSessaoObj.isBefore(dayjs());
        }

        return false;
    }

    get isPermitidaApreciacaoProcessos() {
        return !this.existeSessaoPJe || this.isPermitidaApreciacaoProcessosPJe;
    }

    get isPermitidaApreciacaoProcessosPJe() {
        return this.sessaoStore.isPermitidaApreciacaoProcessosPJe;
    }

    isPermitidaApreciacaoProcesso(registro) {
        return (
            registro &&
            ((registro.processo.origem === Configuracao.ORIGEM_SIJ && !registro.necessitaImportacaoSalaSessao) ||
                (registro.processo.origem === Configuracao.ORIGEM_PJE && this.isPermitidaApreciacaoProcessosPJe))
        );
    }

    get mapaProcessosPartes() {
        return this.sessaoStore.mapaProcessosPartesSessao;
    }

    async carregarMovimentosDecisoesSessao() {
        if (!this.carregandoMovimentosDecisoes) {
            try {
                this.carregandoMovimentosDecisoes = true;
                await this.sessaoStore.carregarMovimentosDecisoesSessaoSelecionada();
            } finally {
                runInAction(() => (this.carregandoMovimentosDecisoes = false));
            }
        }
    }

    async recuperarUrlEdicaoVoto(registro) {
        if (!registro.informacoesAdicionais.urlEdicaoVoto) {
            const urlEdicaoVoto = await this.processoService.recuperarUrlEdicaoVoto(registro);
            runInAction(() => (registro.informacoesAdicionais.urlEdicaoVoto = urlEdicaoVoto));
        }
    }

    carregarAutoTexto = async () => {
        const autoTexto = await this.sessaoService.recuperarAutoTexto(this.usuarioStore.usuario.localizacao);
        runInAction(() => (this.autoTexto = autoTexto));
    };
}
